import {I18n} from '@aws-amplify/core';

import {SimplePageHeader} from '@flexinet/ui-components';
import {AiOutlineBank} from 'react-icons/ai';

import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions';
import {rbacActions} from '../../../config/rbac/actions';
import {ReloadRecordsButton} from '../buttons/ReloadRecordsButton';
import {DeleteRecordButton} from '../buttons/DeleteRecordButton';
import {AddRecordButton} from '../buttons/AddRecordButton';
import {FilterRecordsButton} from '../buttons/FilterRecordsButton';

/**
 * The index toolbar, composed in one place so what this module offers is
 * readable at a glance (§2).
 *
 * Order is [Reload, Filter] then Delete/Add — matching the sibling modules in
 * this app and console-app's `applications` / `accounts` / `organizations`.
 * Do not reorder without a reason (§2).
 *
 * Buttons are HIDDEN, not disabled, when RBAC forbids the action (§2, §10).
 * Filter and Reload are ungated: both act on the list the operator can already
 * see, so there is nothing to withhold.
 */
const useTools = (selectedIds: string[], onDeleteComplete?: () => void) => {

    const {allowedActions} = useVerifiedPermissions();

    const tools = [
        <ReloadRecordsButton key='reload'/>,
        <FilterRecordsButton key='filter'/>,
    ];

    if (allowedActions.includes(rbacActions.DeleteSubscriptionItem)) {
        tools.push(
            <DeleteRecordButton
                key='delete'
                selectedIds={selectedIds}
                onDeleteComplete={onDeleteComplete}
            />,
        );
    }

    if (allowedActions.includes(rbacActions.CreateSubscriptionItem)) {
        tools.push(<AddRecordButton key='add'/>);
    }

    return tools;
};

interface IndexPageHeaderProps {
    selectedIds?: string[];
    onDeleteComplete?: () => void;
}

export const IndexPageHeader = ({
                                    selectedIds = [],
                                    onDeleteComplete,
                                }: IndexPageHeaderProps) => {
    return (
        <SimplePageHeader
            title={I18n.get('Subscription Items')}
            icon={<AiOutlineBank/>}
            tools={useTools(selectedIds, onDeleteComplete)}
        />
    );
};
